import React, { useEffect, useState } from 'react';
import { mockSlots } from '../../../../dummy';
import DeleteIcon from '../../../../svg/DeleteIcon';
import EditIcon from '../../../../svg/EditIcon'; // You'll need an edit icon
import { GenericModal } from '../../../components/common/Modal/GenericModal';
import { showErrorToast, showSuccessToast } from '../../../components/PluginsMenu/Toastr/Toastr';
import { ConfirmActionModal } from '../../../components/common/Modal/ConfirmActionModal';
import { useCreateSlotPricingMutation, useDeleteSlotPricingMutation, useGetAllSlotPricingQuery, useUpdateSlotPricingMutation } from '../../../../store/services/admin/SlotPriceService';

export const SlotPricing = () => {
  const [slotRates, setSlotRates] = useState([]);
  const [newSlot, setNewSlot] = useState({ duration: '', name: '', price: '' });
  const [editingSlot, setEditingSlot] = useState(null); // State for editing
  const [formErrors, setFormErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false); // Modal for editing

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState(null);

  const { data, isLoading, refetch } = useGetAllSlotPricingQuery(undefined, {
    refetchOnMountOrArgChange: true, // This ensures data is refetched when component mounts
  });
  const [createSlot] = useCreateSlotPricingMutation();
  const [deleteSlotPricing] = useDeleteSlotPricingMutation();
  const [updateSlotPricing] = useUpdateSlotPricingMutation(); // New mutation for update
  
  useEffect(() => {
    if (data?.data) {
      setSlotRates(data?.data);
    }
  }, [data?.data]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const validateForm = (slot, isEditing = false) => {
    let errors = {};
    let isValid = true;

    if (!slot.duration) {
      errors.duration = "Duration is required";
      isValid = false;
    } else if (parseInt(slot.duration) <= 0) {
      errors.duration = "Duration must be greater than 0";
      isValid = false;
    } else if (parseInt(slot.duration) > 240) {
      errors.duration = "Duration must not exceed 240 minutes";
      isValid = false;
    } else if (slotRates.find(s => s.duration === parseInt(slot.duration) && (!isEditing || s.id !== slot.id))) {
      errors.duration = `Duration ${slot.duration} already exists`;
      isValid = false;
    }

    if (!slot.price) {
      errors.price = "Price is required";
      isValid = false;
    } else if (parseFloat(slot.price) < 0) {
      errors.price = "Price cannot be negative";
      isValid = false;
    } else if (parseFloat(slot.price) > 500) {
      errors.price = "Price must not exceed $500";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle input changes for both add and edit
  const handleInputChange = (e, isEditing = false) => {
    const { name, value } = e.target;
    
    if (isEditing) {
      setEditingSlot({ ...editingSlot, [name]: value });
    } else {
      setNewSlot({ ...newSlot, [name]: value });
    }

    // Clear errors when user types something
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  // Handle form submission for new slot
  const handleSave = async () => {
    if (!validateForm(newSlot)) {
      return;
    }

    const response = await createSlot({
      duration: parseInt(newSlot.duration), 
      price: parseFloat(newSlot.price)
    });
    
    if (response?.data?.succeeded) {
      const newSlotData = {
        id: response.data.data.id,
        duration: parseInt(newSlot.duration),
        name: newSlot.name,
        price: parseFloat(newSlot.price)
      };
      setSlotRates([...slotRates, newSlotData]);
      await refetch(); // Refetch fresh data from server
      showSuccessToast("New slot price added successfully");
      setNewSlot({ duration: '', name: '', price: '' });
      setShowModal(false);
    } else {
      showErrorToast(response?.error?.data?.errors[0] || 'Failed to create slot price');
    }
  };

  // Handle update slot pricing
  const handleUpdate = async () => {
    if (!validateForm(editingSlot, true)) {
      return;
    }

    const response = await updateSlotPricing({
      id: editingSlot.id,
      duration: parseInt(editingSlot.duration),
      price: parseFloat(editingSlot.price)
    });

    if (response?.data?.succeeded) {
      // Update the slot in local state
      const updatedSlots = slotRates.map(slot => 
        slot.id === editingSlot.id 
          ? { ...slot, duration: parseInt(editingSlot.duration), price: parseFloat(editingSlot.price) }
          : slot
      );
      
      setSlotRates(updatedSlots);
      await refetch()
      showSuccessToast("Slot price updated successfully");
      setShowEditModal(false);
      setEditingSlot(null);
    } else {
      showErrorToast(response?.error?.data?.errors[0] || 'Failed to update slot price');
    }
  };

  // Open edit modal
  const handleEdit = (slot) => {
    setEditingSlot({
      id: slot.id,
      duration: slot.duration.toString(),
      price: slot.price.toString()
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingSlot(null);
    setFormErrors({});
  };

  // Open delete confirmation modal
  const confirmDelete = (id) => {
    setSlotToDelete(id);
    setShowDeleteModal(true);
  };

  // Delete a price after confirmation
  const deleteRate = async () => {
    const response = await deleteSlotPricing({ Id: slotToDelete });

    if (response?.data?.succeeded) {
      setSlotRates(slotRates.filter(slot => slot.id !== slotToDelete));
      await refetch();
      showSuccessToast("Slot price removed successfully");
      setShowDeleteModal(false);
      setSlotToDelete(null);
    } else {
      showErrorToast(response?.error?.data?.errors[0] || 'Failed to delete slot price');
    }
  };

  // Modal open/close handlers
  const handleOpenModal = () => {
    setFormErrors({});
    setNewSlot({ duration: '', name: '', price: '' });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormErrors({});
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSlotToDelete(null);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="container-fluid p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading slot pricing data...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      {/* Current Pricing Table */}
      <div className="row mb-4">
        <div className="col">
          <div className="card shadow-sm">
            <div className="card-body">
              {slotRates.length === 0 ? (
                <div className="alert alert-info">
                  No slot prices defined yet. Click "Add New Slot Price" to create one.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Duration (min)</th>
                        <th>Price ($)</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slotRates.map((slot) => (
                        <tr key={slot?.id}>
                          <td>{slot?.duration} min</td>
                          <td>${slot?.price?.toFixed(2)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <span 
                                onClick={() => handleEdit(slot)} 
                                style={{ cursor: 'pointer' }}
                                title="Edit slot price"
                              >
                                <EditIcon fill="#007bff" width={18} height={18} />
                              </span>
                              <span 
                                onClick={() => confirmDelete(slot?.id)} 
                                style={{ cursor: 'pointer' }}
                                title="Delete slot price"
                              >
                                <DeleteIcon fill="#ff0000" width={18} height={18} />
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="d-flex justify-content-end align-items-center">
                <button className="btn btn-primary" onClick={handleOpenModal}>
                  Add New Slot Price
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for adding new slot price */}
      <GenericModal
        show={showModal}
        handleClose={handleCloseModal}
        handleSave={handleSave}
        title="Add New Slot Price"
        createConfig="Create Slot"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <div className="mb-3">
            <label htmlFor="duration" className="form-label">Duration (minutes)</label>
            <input
              type="number"
              className={`form-control ${formErrors.duration ? 'is-invalid' : ''}`}
              id="duration"
              name="duration"
              value={newSlot.duration}
              onChange={handleInputChange}
              min="1"
              max="240"
            />
            {formErrors.duration && <div className="invalid-feedback">{formErrors.duration}</div>}
          </div>
          <div className="mb-3">
            <label htmlFor="price" className="form-label">Price ($)</label>
            <div className="input-group has-validation">
              <span className="input-group-text">$</span>
              <input
                type="number"
                className={`form-control ${formErrors.price ? 'is-invalid' : ''}`}
                id="price"
                name="price"
                value={newSlot.price}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                max="500"
              />
              {formErrors.price && <div className="invalid-feedback">{formErrors.price}</div>}
            </div>
          </div>
        </form>
      </GenericModal>

      {/* Modal for editing slot price */}
      <GenericModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        handleSave={handleUpdate}
        title="Edit Slot Price"
        createConfig="Update Slot"
      >
        {editingSlot && (
          <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
            <div className="mb-3">
              <label htmlFor="edit-duration" className="form-label">Duration (minutes)</label>
              <input
                type="number"
                className={`form-control ${formErrors.duration ? 'is-invalid' : ''}`}
                id="edit-duration"
                name="duration"
                value={editingSlot.duration}
                onChange={(e) => handleInputChange(e, true)}
                min="1"
                max="240"
              />
              {formErrors.duration && <div className="invalid-feedback">{formErrors.duration}</div>}
            </div>
            <div className="mb-3">
              <label htmlFor="edit-price" className="form-label">Price ($)</label>
              <div className="input-group has-validation">
                <span className="input-group-text">$</span>
                <input
                  type="number"
                  className={`form-control ${formErrors.price ? 'is-invalid' : ''}`}
                  id="edit-price"
                  name="price"
                  value={editingSlot.price}
                  onChange={(e) => handleInputChange(e, true)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  max="500"
                />
                {formErrors.price && <div className="invalid-feedback">{formErrors.price}</div>}
              </div>
            </div>
          </form>
        )}
      </GenericModal>

      {/* Confirmation Modal for Deletion */}
      <ConfirmActionModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        onConfirm={deleteRate}
        title="Delete Slot Price"
        message="Are you sure you want to delete this slot price? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};